import type { ISocialApplyOffer, TSocialOfferSpotlight } from "@api";
import type { ICandidate } from "@bbj-types/candidate";
import { FollowUpSocialOffer } from "@candidate/details/views/socialOffer/FollowUpSocialOffer";
import { TypoTooltip } from "@data-display/wrapper/DetectOverflow";
import { HoverHandler, HoverHandlerMaster } from "@data-display/wrapper/HoverHandler";
import { IconButtonVariant } from "@inputs/button/IconButtonVariant";
import { NoteAddOutlined } from "@mui/icons-material";
import DomainIcon from "@mui/icons-material/Domain";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import LocalPhoneOutlinedIcon from "@mui/icons-material/LocalPhoneOutlined";
import StarIcon from "@mui/icons-material/Star";
import { Chip, IconButton, Stack, Tooltip, Typography } from "@mui/material";
import { Authorizer } from "@navigations/Authorizer";
import { LinkStyless } from "@navigations/LinkStyless";
import { useGlobalContext } from "@root/context";
import { isInternalOfferSource } from "@root/utils/enums/offer";
import { useI18nContext } from "@root/utils/organisation";
import { formatPhoneNumber } from "@root/utils/phoneLib";
import type { Dispatch, ReactNode, SetStateAction } from "react";
import { FollowUpSocialOfferV2 } from "../FollowUpSocialOfferV2";
import { useCardSocialOffer } from "./logics";
import { CardSpotlightSocialOfferStack, classes } from "./styles";
import { socialOfferDate } from "./utils";

export interface ICardSpotlightSocialOfferProps {
  offer: TSocialOfferSpotlight;
  actions?: ReactNode[];
  openDrawer: () => void;
  mode: "link" | "drawer";
  candidate?: ICandidate;
  updateSocialOffersSpotlight?: Dispatch<SetStateAction<TSocialOfferSpotlight[]>>;
  setOfferFupModal?: Dispatch<SetStateAction<ISocialApplyOffer | null>>;
  displayFup?: boolean;
}
export const CardSpotlightSocialOffer = ({
  offer,
  actions,
  openDrawer,
  candidate,
  mode,
  updateSocialOffersSpotlight,
  setOfferFupModal,
  displayFup,
}: ICardSpotlightSocialOfferProps) => {
  const { challenges } = useGlobalContext();
  const { timeZone } = useI18nContext();
  const { miscellaneousInformations, isAuthorizedActionV2 } = useCardSocialOffer(offer, challenges);
  const notPermanent = Boolean(offer.serviceEndDate && offer.serviceStartDate);
  const expired = offer.serviceEndDate && offer.serviceEndDate < new Date();

  const card = (
    <HoverHandlerMaster className={classes.hover}>
      <CardSpotlightSocialOfferStack
        className={classes.root}
        onClick={
          mode === "drawer"
            ? (e) => {
                e.preventDefault();
                openDrawer();
              }
            : undefined
        }
      >
        <Stack
          direction="row"
          sx={{
            justifyContent: "space-between",
            alignItems: "flex-start",
          }}
        >
          <Stack
            sx={{
              alignItems: "flex-start",
            }}
          >
            <Tooltip
              disableHoverListener={!expired}
              title={
                offer.serviceEndDate &&
                `L’offre a expiré le ${offer.serviceEndDate.toLocaleDateString("fr-FR", {
                  timeZone,
                })}, elle sera automatiquement archivée et disparaîtra des résultats de recherche le ${new Date(
                  new Date(offer.serviceEndDate).setMonth(offer.serviceEndDate.getMonth() + 1)
                ).toLocaleDateString("fr-FR", {
                  timeZone,
                })}. Vous pouvez toujours la dupliquer pour la créer avec de nouvelles dates.`
              }
            >
              <Chip
                size="small"
                color={notPermanent ? "blue" : "primary"}
                sx={(theme) => ({
                  background: expired ? theme.palette.gray.dark : "",
                  padding: "4px",
                })}
                label={
                  <Typography variant="body2SemiBold">
                    {expired ? "Offre expirée" : notPermanent ? socialOfferDate(offer, timeZone) : "Permanente"}
                  </Typography>
                }
              />
            </Tooltip>
            <Typography
              variant="body2SemiBold"
              sx={{
                color: "black.light",
              }}
            >
              {isInternalOfferSource(offer.source) ? "Offre manuelle" : `Par ${offer.source}`}
            </Typography>
          </Stack>
          <HoverHandler>
            <Stack
              direction="row"
              sx={{
                gap: 1,
                alignItems: "center",
              }}
            >
              {offer.favoriteChallenges.length > 0 && <StarIcon key={offer.offerId} color="yellow" />}
              {actions}
            </Stack>
          </HoverHandler>
        </Stack>
        <Stack>
          <Typography variant="subtitle2SemiBold" noWrap>
            {offer.name}
          </Typography>
          <Typography variant="body1" noWrap>
            {miscellaneousInformations}
          </Typography>
        </Stack>
        <Stack
          sx={{
            gap: 1,
            flexDirection: "row",
            alignItems: "center",
          }}
        >
          {offer.provider?.name && (
            <TypoTooltip noWrap>
              <Stack
                direction="row"
                sx={{
                  gap: 1,
                }}
              >
                <DomainIcon color="secondary" fontSize="small" />
                <Typography variant="body1" noWrap>
                  {offer.provider.name}
                </Typography>
              </Stack>
            </TypoTooltip>
          )}
          {offer.contact?.phone && (
            <HoverHandler>
              <Tooltip title={formatPhoneNumber(offer.contact.phone)}>
                <IconButtonVariant
                  variant="elevated"
                  color="success"
                  size="small"
                  LinkComponent="a"
                  href={`tel:${offer?.contact?.phone}`}
                  onClick={(e) => e.stopPropagation()}
                >
                  <LocalPhoneOutlinedIcon htmlColor="white" />
                </IconButtonVariant>
              </Tooltip>
            </HoverHandler>
          )}
          {offer.contact?.mail && (
            <HoverHandler>
              <Tooltip title={offer.contact.mail}>
                <IconButtonVariant
                  variant="elevated"
                  color="success"
                  size="small"
                  LinkComponent="a"
                  href={`mailto:${offer?.contact?.mail}`}
                  onClick={(e) => e.stopPropagation()}
                >
                  <EmailOutlinedIcon htmlColor="white" />
                </IconButtonVariant>
              </Tooltip>
            </HoverHandler>
          )}
        </Stack>
        <Stack
          sx={{
            gap: 1,
            flexDirection: "row",
            justifyContent: "end",
            alignItems: "center",
          }}
        >
          {!isAuthorizedActionV2 && (
            <Authorizer requiredPermissions={["offers.social.prescription.operator"]}>
              {displayFup && (
                <Tooltip title="Réaliser la prescription">
                  <HoverHandler>
                    <span>
                      <IconButton
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setOfferFupModal?.(offer);
                        }}
                      >
                        <NoteAddOutlined color="primary" />
                      </IconButton>
                    </span>
                  </HoverHandler>
                </Tooltip>
              )}
            </Authorizer>
          )}
          <Authorizer requiredPermissions={["candidate.socialboard.apply.modify"]}>
            {candidate && (
              <HoverHandler>
                {isAuthorizedActionV2 ? (
                  <FollowUpSocialOfferV2
                    offer={offer}
                    candidate={candidate}
                    expired={false}
                    displayFup={displayFup}
                    setOfferFupModal={setOfferFupModal}
                    onUpdateOffer={(offer) => {
                      if (updateSocialOffersSpotlight) {
                        updateSocialOffersSpotlight((offers) => offers?.filter((o) => o.offerId !== offer.offerId));
                      }
                    }}
                  />
                ) : (
                  <FollowUpSocialOffer
                    offer={offer}
                    candidate={candidate}
                    expired={false}
                    onUpdateOffer={(offer) => {
                      if (updateSocialOffersSpotlight) {
                        updateSocialOffersSpotlight((offers) => offers?.filter((o) => o.offerId !== offer.offerId));
                      }
                    }}
                  />
                )}
              </HoverHandler>
            )}
          </Authorizer>
        </Stack>
      </CardSpotlightSocialOfferStack>
    </HoverHandlerMaster>
  );

  if (mode === "link") {
    return (
      <LinkStyless rel="noreferrer noopener" target="_blank" to={`/offer/social/${offer.offerId}`}>
        {card}
      </LinkStyless>
    );
  }

  return card;
};
